import React from 'react';
import {
  ClockIcon,
  EnvelopeIcon,
  FunnelIcon,
  InboxIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { getCaraTriggerEmailAddress } from '@oysterjs/core/email';
import { Badge } from '@oysterjs/ui/badge';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  TriggerEmailAddressKind,
  TriggerEmailPrivacy,
  TriggerEmailScopeMode,
  useGetCustomPresetsQuery,
  useGetTriggerEmailFoldersQuery,
  type CaraWorkspacePreset,
  type TriggerCallRouting,
  type TriggerCallType,
  type CaraWorkspaceUseCase,
  type GetTriggerEmailBuilderOptionsQuery,
  type TriggerEmailRuleInput,
  type TriggerEmailSelectedFoldersInput
} from '~/api/graphql';
import { type EmailConfigDraft } from '~/api/data/triggers/email-config';
import {
  describeEmailArrivals,
  describeEmailFolders,
  describeEmailMailboxesInSentence,
  describeEmailRule,
  folderLabel
} from '~/api/data/triggers/email-summary';
import {
  builtInTaskTitle,
  CALL_ROUTING_LABELS,
  CALL_TYPE_LABELS,
  cronToSchedule,
  formatCallDuration,
  formatScheduleDays,
  PRESET_UNAVAILABLE,
  TASK_ICONS,
  useMailboxLabel
} from '~/pages/workflows/trigger-utils';
import { Chips, FlowStep, RuleLines, SOURCE_ICON_CLASS } from '~/components/triggers/flow-step';

// A selected mailbox's folders by name. Folder names are listed from the provider on
// demand (never stored), so until they load, or if they cannot, show how many.
const EmailAccountFolders = (props: {
  accountId: string;
  folders: TriggerEmailSelectedFoldersInput;
  mailbox?: string;
  canReadSetup: boolean;
}) => {
  const { accountId, folders, canReadSetup } = props;
  const query = useGetTriggerEmailFoldersQuery({
    variables: { accountId },
    skip: !canReadSetup || !!folders.allFolders || (folders.ids ?? []).length === 0,
    fetchPolicy: 'cache-first',
    errorPolicy: 'none'
  });
  const names = React.useMemo(
    () =>
      new Map(
        (canReadSetup ? (query.data?.triggerEmailFolders ?? []) : []).map((folder) => [
          folder.id,
          folderLabel(folder)
        ])
      ),
    [query.data, canReadSetup]
  );
  const labels = describeEmailFolders(
    { scope: { mode: TriggerEmailScopeMode.Selected, accounts: [{ accountId, folders }] } },
    { folder: (_, id) => names.get(id) }
  );
  return (
    <div className="space-y-1">
      {props.mailbox && <p className="m-0 text-sm">{props.mailbox}</p>}
      <Chips items={labels} />
    </div>
  );
};

export const EmailWhenStep = (props: {
  draft: EmailConfigDraft;
  mailboxLabel: (accountId: string) => string;
  canReadSetup: boolean;
}) => {
  const { draft } = props;
  const accounts = draft.scope.accounts ?? [];
  return (
    <FlowStep label="When" icon={InboxIcon} iconClassName={SOURCE_ICON_CLASS.accent}>
      <p className="m-0 text-sm font-medium">
        An email arrives in{' '}
        {describeEmailMailboxesInSentence(draft, { mailbox: props.mailboxLabel })}
      </p>
      {draft.scope.mode === TriggerEmailScopeMode.All ? (
        <Chips items={describeEmailFolders(draft)} />
      ) : (
        accounts.map((account) => (
          <EmailAccountFolders
            key={account.accountId}
            accountId={account.accountId}
            folders={account.folders}
            canReadSetup={props.canReadSetup}
            mailbox={accounts.length > 1 ? props.mailboxLabel(account.accountId) : undefined}
          />
        ))
      )}
      <Chips items={[describeEmailArrivals(draft)]} />
    </FlowStep>
  );
};

// Sender and recipient conditions may name agency users or access profiles by ID.
export const namesPeople = (rule: TriggerEmailRuleInput | null | undefined): boolean =>
  !!rule &&
  ([rule.sender?.kind, rule.recipient?.match.kind].some(
    (kind) =>
      kind === TriggerEmailAddressKind.User || kind === TriggerEmailAddressKind.AccessProfile
  ) ||
    (rule.children ?? []).some(namesPeople));

export type EmailDetailOptions = GetTriggerEmailBuilderOptionsQuery['triggerEmailBuilderOptions'];

export const EmailIfStep = ({
  draft,
  options,
  loading
}: {
  draft: EmailConfigDraft;
  options?: EmailDetailOptions;
  loading: boolean;
}) => {
  const { rule } = draft;
  const needsNames = namesPeople(rule);
  const lines = React.useMemo(
    () =>
      describeEmailRule(rule, {
        user: (id) => options?.users.find((user) => user.id === id)?.name,
        profile: (id) => options?.profiles.find((profile) => profile.id === id)?.name
      }),
    [rule, options]
  );
  return (
    <FlowStep label="If" icon={FunnelIcon} iconClassName={SOURCE_ICON_CLASS.neutral}>
      {needsNames && loading && !options ? (
        <Spinner size="size-4" color="neutral" />
      ) : lines.length ? (
        <RuleLines lines={lines} />
      ) : (
        <p className="m-0 text-sm">Any email that arrives</p>
      )}
      {draft.timezone && <Chips items={[`Time zone: ${draft.timezone}`]} />}
    </FlowStep>
  );
};

export const WORKSPACE_PRIVACY_LABELS: Record<TriggerEmailPrivacy, string> = {
  [TriggerEmailPrivacy.MatchedMailboxOwners]: 'Matched mailbox owners and agency admins',
  [TriggerEmailPrivacy.Agency]: 'Everyone in agency'
};

export const ThenStep = ({
  target,
  privacy
}: {
  target: {
    presetId?: string | null;
    preset?: CaraWorkspacePreset | null;
    useCase?: CaraWorkspaceUseCase | null;
    prompt?: string | null;
  };
  /** Email triggers only: who can open the launched workspace. */
  privacy?: TriggerEmailPrivacy | null;
}) => {
  const { data, loading } = useGetCustomPresetsQuery({ skip: !target.presetId });
  const customPreset = target.presetId
    ? data?.customPresets.find((preset) => preset.id === target.presetId)
    : undefined;
  const builtInTitle = builtInTaskTitle(target);
  const kind = builtInTitle ? 'builtIn' : target.presetId ? 'custom' : 'prompt';
  const { icon, className } = TASK_ICONS[kind];

  let headline: React.ReactNode;
  if (builtInTitle) {
    headline = <p className="m-0 text-sm font-medium">{builtInTitle}</p>;
  } else if (target.presetId) {
    headline = customPreset ? (
      <p className="m-0 flex flex-wrap items-center gap-2 text-sm font-medium">
        <span>{customPreset.title}</span>
        <Badge color="purple">Custom</Badge>
      </p>
    ) : loading ? (
      <div className="w-4">
        <Spinner size="size-4" color="neutral" />
      </div>
    ) : (
      <p className="m-0 italic text-neutral-400 dark:text-neutral-500">{PRESET_UNAVAILABLE}</p>
    );
  } else {
    headline = target.prompt ? (
      <p className="m-0 text-sm font-medium">Custom instructions</p>
    ) : (
      <p className="m-0 text-neutral-500 dark:text-neutral-400">No task configured</p>
    );
  }

  return (
    <FlowStep label="Then" icon={icon} iconClassName={className}>
      {headline}
      {target.prompt && !loading && (
        <div>
          {kind !== 'prompt' && (
            <p className="m-0 mb-1 text-xs text-neutral-500 dark:text-neutral-400">
              With additional instructions
            </p>
          )}
          <blockquote
            title={target.prompt}
            className="m-0 line-clamp-3 whitespace-pre-wrap border-l-2 border-neutral-200 pl-3 text-neutral-600 dark:border-neutral-600 dark:text-neutral-300"
          >
            {target.prompt}
          </blockquote>
        </div>
      )}
      {privacy && <Chips items={[`Workspace privacy: ${WORKSPACE_PRIVACY_LABELS[privacy]}`]} />}
    </FlowStep>
  );
};

export type WhenMatcher = {
  caraAddressConfig?: { emailSuffix: string } | null;
  mailboxConfig?: { integrationId: string; folder?: string | null } | null;
  scheduleConfig?: { cron: string; timezone: string } | null;
  callAnalyzedConfig?: {
    callType?: TriggerCallType | null;
    routing?: TriggerCallRouting | null;
    minimumDurationSeconds?: number | null;
  } | null;
};

export const WhenStep = (props: { matcher: WhenMatcher; emailNamespace: string }) => {
  const { matcher } = props;
  // Legacy mailboxConfig keeps its existing send-mailbox label behavior.
  const mailboxLabel = useMailboxLabel(!matcher.mailboxConfig);

  if (matcher.caraAddressConfig) {
    return (
      <FlowStep label="When" icon={EnvelopeIcon} iconClassName={SOURCE_ICON_CLASS.accent}>
        <p className="m-0 text-sm font-medium">An email arrives at</p>
        {props.emailNamespace ? (
          <Chips
            items={[
              getCaraTriggerEmailAddress(
                props.emailNamespace,
                matcher.caraAddressConfig.emailSuffix
              )
            ]}
          />
        ) : (
          <p className="m-0 text-amber-700 dark:text-amber-300">
            Claim an agency email handle in settings to show this address.
          </p>
        )}
      </FlowStep>
    );
  }

  if (matcher.mailboxConfig) {
    const { integrationId, folder } = matcher.mailboxConfig;
    return (
      <FlowStep label="When" icon={InboxIcon} iconClassName={SOURCE_ICON_CLASS.accent}>
        <p className="m-0 text-sm font-medium">An email arrives in {mailboxLabel(integrationId)}</p>
        {folder && <Chips items={[`Folder: ${folder}`]} />}
      </FlowStep>
    );
  }

  if (matcher.scheduleConfig) {
    const { cron, timezone } = matcher.scheduleConfig;
    const schedule = cronToSchedule(cron);
    return (
      <FlowStep label="When" icon={ClockIcon} iconClassName={SOURCE_ICON_CLASS.neutral}>
        <p className="m-0 text-sm font-medium">
          {schedule
            ? `${formatScheduleDays(schedule.days)} at ${schedule.time}`
            : 'On a custom schedule'}
        </p>
        <Chips items={schedule ? [timezone] : [cron, timezone]} />
      </FlowStep>
    );
  }

  if (matcher.callAnalyzedConfig) {
    const call = matcher.callAnalyzedConfig;
    const filters = [
      call.callType && CALL_TYPE_LABELS[call.callType],
      call.routing && CALL_ROUTING_LABELS[call.routing]
    ].filter((label): label is string => !!label);
    if (call.minimumDurationSeconds && call.minimumDurationSeconds > 0) {
      filters.push(`At least ${formatCallDuration(call.minimumDurationSeconds)}`);
    }
    return (
      <FlowStep label="When" icon={PhoneIcon} iconClassName={SOURCE_ICON_CLASS.accent}>
        <p className="m-0 text-sm font-medium">A call is completed</p>
        <Chips items={filters} />
      </FlowStep>
    );
  }

  return null;
};
